/**
 * EAWelcomeModal.jsx - Early Access Welcome Modal
 *
 * Shows ONCE on first launch after intro sequence completes.
 * Encourages players to join Discord for bug reports and community.
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const DISCORD_INVITE_URL = 'https://discord.gg/SVkBsn688z';

export default function EAWelcomeModal({ isOpen, onContinue }) {
  const { t } = useTranslation();

  const handleJoinDiscord = () => {
    window.open(DISCORD_INVITE_URL, '_blank', 'noopener,noreferrer');
    // Does NOT close modal - player must click Continue
  };

  const handleContinue = () => {
    // Mark as seen so it never shows again
    localStorage.setItem('hasSeenEAWelcome', 'true');
    onContinue();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center"
        style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(20, 30, 48, 0.95) 0%, rgba(0, 0, 0, 0.98) 70%)',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.1, duration: 0.4, ease: 'easeOut' }}
          className="max-w-lg mx-4 text-center"
        >
          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-3xl md:text-4xl font-light mb-8 tracking-wider"
            style={{ color: '#60ffd0' }}
          >
            {t('eaWelcome.title')}
          </motion.h1>

          {/* Main message */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="space-y-4 text-lg text-gray-300 mb-8"
          >
            <p>{t('eaWelcome.thankYou')}</p>
            <p>{t('eaWelcome.wantFun')}</p>
          </motion.div>

          {/* Discord CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mb-8"
          >
            <p className="text-xl text-white font-semibold mb-4">
              {t('eaWelcome.joinDiscord')}
            </p>
            <p className="text-gray-400 mb-6">{t('eaWelcome.heresWhy')}</p>

            <div className="text-left max-w-sm mx-auto space-y-3 mb-6">
              <div className="flex items-start gap-3">
                <span style={{ color: '#60ffd0' }}>&#8226;</span>
                <span className="text-gray-300">{t('eaWelcome.bullet1')}</span>
              </div>
              <div className="flex items-start gap-3">
                <span style={{ color: '#60ffd0' }}>&#8226;</span>
                <span className="text-gray-300">{t('eaWelcome.bullet2')}</span>
              </div>
            </div>
          </motion.div>

          {/* The "NOW" message */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-gray-400 mb-6 space-y-2"
          >
            <p>{t('eaWelcome.noTimelines')}</p>
            <p>
              {t('eaWelcome.weWorkOff')} <span className="text-white font-bold">{t('eaWelcome.now')}</span>.
            </p>
            <p className="mt-4">{t('eaWelcome.bugToday')}</p>
          </motion.div>

          {/* Discord Button */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            onClick={handleJoinDiscord}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full max-w-xs mx-auto block px-8 py-4 text-lg font-bold tracking-wider rounded-lg mb-4"
            style={{
              background: '#5865F2', // Discord blurple
              color: '#fff',
              boxShadow: '0 0 20px rgba(88, 101, 242, 0.4)',
            }}
          >
            {t('eaWelcome.joinDiscordButton')}
          </motion.button>

          {/* Benefits note */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="text-gray-500 text-sm mb-8"
          >
            {t('eaWelcome.benefitsAll')}
          </motion.p>

          {/* Continue Button */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            onClick={handleContinue}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="px-8 py-3 text-sm tracking-widest border rounded"
            style={{
              borderColor: '#60ffd0',
              color: '#60ffd0',
              backgroundColor: 'transparent',
            }}
          >
            {t('eaWelcome.continue')} &#8594;
          </motion.button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/**
 * Check if EA Welcome modal should be shown
 */
export function shouldShowEAWelcome() {
  return localStorage.getItem('hasSeenEAWelcome') !== 'true';
}
